<template>
  <div>
    <exo-table overviewAction :root="_self">
      <exo-form :root="_self">
        <template v-slot:titleTooltip>
          <exo-help-dialog
            :color="'black--text'"
            :margin="'m-0'"
            title="Earth-Moon Trajectory Definition"
          >
            <p>
              Beta development : Generate a low-thrust trajectory to the Moon.
            </p>
            <h3>Hypotheses</h3>
            <p>The underlying assumptions for this beta are the following:</p>
            <ul>
              <li>
                The Earth geometry is considered spherical as well as the Moon's
              </li>
              <li>
                Only circular orbits are considered for the initial and target
                orbits
              </li>
              <li>The trajectory is generated in the CR3BP</li>
              <li>
                The thrust level is constant equal to the maximum thrust during
                thrust arcs
              </li>
              <li>
                The thrust-to-weight ratio is bounded between [1e-4, 1e-3]
              </li>
              <li>The specific impulse is bounded between [800s, 10000s]</li>
            </ul>
          </exo-help-dialog>
        </template>
        <exo-input-name :root="_self" />
        <exo-card-form label="Spacecraft">
          <v-row no-gutters justify="start">
            <exo-input-double :root="_self" name="initialSpacecraftMass" />

            <!-- <exo-input-double :root="_self" name="thrust" /> -->
            <exo-input-double :root="_self" name="isp" />
            <exo-input-double :root="_self" name="maximumDutyCycle">
              <exo-help-dialog
                margin="mt-3"
                title="Spacecraft and propulsion system"
              >
                <h3>Spacecraft mass</h3>
                <p>
                  The initial spacecraft mass corresponds to the total mass
                  (including the propulsion system and the propellant) at the
                  beginning of the trajectory.
                </p>
                <h3>Duty cycle</h3>
                <p>
                  The maximum duty cycle stands for the maximum ratio between
                  thrust arcs and coast arcs during one revolution around the
                  Earth or the Moon. The generated trajectory will generate a
                  control sequence with a duty cycle inferior to this value.
                </p>
              </exo-help-dialog>
            </exo-input-double>
          </v-row>
          <v-row>
            <exo-input-slider
              :root="_self"
              v-if="validMass === true"
              :min="minThrustmN"
              :max="maxThrustmN"
              :step="Math.ceil((maxThrustmN - minThrustmN) / 1000)"
              name="thrust"
            >
              <exo-help-dialog title="Nominal thrust">
                <p>
                  This thrust value will be the thrust level during the thrust
                  arcs. The current implementation only supports
                  thrust-to-weight ratios between [1e-4, 1e-3]. For a given
                  spacecraft mass, the minimum and maximum possible values for
                  thrust are then automatically computed and used as limits for
                  the slider selector.
                </p>
              </exo-help-dialog>
            </exo-input-slider>
          </v-row>
        </exo-card-form>
        <exo-card-form label="Initial Orbit">
          <v-row no-gutters justify="start">
            <exo-input-double :root="_self" name="initialOrbitAltitude">
              <exo-help-dialog title="Initial orbit altitude">
                The initial orbit altitude defines the altitude of the departure
                orbit (circular) around the Earth. This orbit will be
                constrained to be in the Earth-Moon plane.
              </exo-help-dialog>
            </exo-input-double>
          </v-row>
        </exo-card-form>
        <exo-card-form label="Final Orbit">
          <v-row no-gutters justify="start">
            <v-col cols="5">
              <exo-input-double :root="_self" name="finalOrbitAltitude">
              </exo-input-double>
            </v-col>
            <v-col cols="1">
              <exo-help-dialog title="Final orbit altitude">
                The final orbit altitude defines the altitude of the target
                orbit (circular) around the Moon.
              </exo-help-dialog>
            </v-col>
            <v-col cols="5">
              <exo-input-double
                :root="_self"
                name="finalOrbitEarthMoonPlaneInclination"
              />
            </v-col>

            <v-col cols="1">
              <exo-help-dialog title="Final orbit relative inclination">
                The final orbit relative inclination stands for the relative
                inclination of the arrival orbit with respect to the Earth-Moon
                plane.
              </exo-help-dialog>
            </v-col>
          </v-row>
        </exo-card-form>
        <exo-form-alert type="error" v-if="false">
          TODO: add constraint on thrust given spacecraft mass
        </exo-form-alert>
      </exo-form>
    </exo-table>
    <exo-modal-references :root="_self" :modalContent="this.modalRef" />
  </div>
</template>

<script>
import formMixin from '../../mixins/forms.js'
import analyticalMixin from '../../mixins/analyticalFoms.js'
import { mapGetters } from 'vuex'
import Vue from 'vue'

export default {
  name: 'earthMoonTrajectoryRequest',

  mixins: [formMixin, analyticalMixin],
  // mixins: [formMixin, numericalMixin],

  computed: {
    ...mapGetters(['currentMission']),

    currentParametricValue() {
      return false
    },

    allowsParametricValues() {
      return false
    },

    validMass() {
      if (this.inputs.initialSpacecraftMass !== '') {
        return this.getFieldState(this.inputs, 'initialSpacecraftMass', true)
          .value
      }
      return false
    },

    minThrustmN() {
      let weight = Number(this.inputs.initialSpacecraftMass) * 9.80665
      if (!isNaN(weight) && weight > 0) {
        return 1e3 * this.minTWratio * weight
      }
      return 0
    },

    maxThrustmN() {
      let weight = Number(this.inputs.initialSpacecraftMass) * 9.80665
      if (!isNaN(weight) && weight > 0) {
        return 1e3 * this.maxTWratio * weight
      }
      return 0
    }
  },

  data() {
    return {
      tableFields: [
        { value: 'displayedName', text: 'Name', sortable: true },
        {
          value: 'initialSpacecraftMass',
          text:
            'Initial mass including propellant' +
            this.getDefaultUnitLabel('initialSpacecraftMass'),
          sortable: true
        },
        {
          value: 'thrust',
          text: 'Nominal thrust' + this.getDefaultUnitLabel('thrust'),
          sortable: true
        },
        {
          value: 'isp',
          text: 'Specific impulse' + this.getDefaultUnitLabel('isp'),
          sortable: true
        },
        {
          value: 'maximumDutyCycle',
          text:
            'Maximum duty cycle' + this.getDefaultUnitLabel('maximumDutyCycle'),
          sortable: true
        },
        {
          value: 'initialOrbitAltitude',
          text: 'Altitude' + this.getDefaultUnitLabel('initialOrbitAltitude'),
          sortable: true
        },
        {
          value: 'finalOrbitAltitude',
          text: 'Altitude' + this.getDefaultUnitLabel('finalOrbitAltitude'),
          sortable: true
        },
        {
          value: 'finalOrbitEarthMoonPlaneInclination',
          text:
            'Inclination with respect to the Earth-Moon plan' +
            this.getDefaultUnitLabel('finalOrbitEarthMoonPlaneInclination'),
          sortable: true
        },

        { value: 'actions', text: 'Actions', sortable: false }
      ],

      modalRef: { content: [] },
      showModal: false,

      inputs: {
        initialSpacecraftMass: this.getDefaultValue('initialSpacecraftMass'),
        thrust: this.getDefaultValue('thrust'),
        isp: this.getDefaultValue('isp'),
        maximumDutyCycle: this.getDefaultValue('maximumDutyCycle'),
        initialOrbitAltitude: this.getDefaultValue('initialOrbitAltitude'),
        finalOrbitAltitude: this.getDefaultValue('finalOrbitAltitude'),
        finalOrbitEarthMoonPlaneInclination: this.getDefaultValue(
          'finalOrbitEarthMoonPlaneInclination'
        )
      },

      minTWratio: 1e-4,
      maxTWratio: 1e-3
    }
  },

  methods: {
    getCurrentFormType() {
      return 'EarthMoonTrajectoryRequest'
    },

    getRefList(param) {
      return []
    },
    overview(mission) {
      this.sendMissionEventAfterRedirectionToDashboard(
        'set-current-mission',
        mission
      )
    },

    sendMissionEventAfterRedirectionToDashboard(eventName, mission) {
      this.resetCurrentMission()
      this.$router.push({
        path: '/' + 'earthmoontrajectorydashboard'
      })
      // event send to root because it should be treated by sub components
      // use nextTick to be sure mission components are created and can handle events
      Vue.nextTick(() => {
        this.$root.$emit(eventName, mission)
      })
    },
    resetCurrentMission() {
      // immediate effect
      this.$root.$emit('set-current-mission', null)
    },

    getFieldState(parentObject, fieldName, required) {
      let fieldState = analyticalMixin.methods.getFieldState.call(
        this,
        parentObject,
        fieldName,
        required
      )
      return fieldState
    },
    onSave: function (evt) {
      this.inputs.saving = true
      this.$http
        .put(
          this.getCurrentAPI() + '/' + this.inputs.id,
          this.createObjectFromForm()
        )
        .then((response) => {
          this.inputs.saving = false

          //this.$root.$emit('update-mission-list')

          this.$root.$emit('set-current-mission', response.data) // update fields and current mission
          // redirect to dashboard

          this.$router.push({ path: '/earthmoontrajectorydashboard' })
        })
        .catch((e) => {
          this.inputs.saving = false
          this.displayMessage(
            'Error creating ' + this.inputs.type + '. ',
            e,
            'notification',
            'error'
          )
        })
    }
  }
}
</script>
